import { useRef, useState } from 'react'
import { showErrorToast } from 'shared/utils/toast.ts'
import { useCallbackRef } from '../useCallbackRef'
import { processErrorResponse } from './utils'

interface Params<R> {
  onSuccess?: (result?: R) => void
  showErrorNotification?: boolean
  onError?: (error?: unknown) => void
  initialLoading?: boolean
}

type PromiseResult<T extends Promise<any>> = T extends Promise<infer R> ? R : never

export const usePromise = <T extends (...args: any[]) => Promise<any>, R = PromiseResult<ReturnType<T>>>(
  cb: T,
  params?: Params<R>
) => {
  const { onSuccess, onError, initialLoading = false, showErrorNotification } = params || {}
  const [isLoading, setLoading] = useState(initialLoading)
  const [data, setData] = useState<R | undefined>()
  const [error, setError] = useState<unknown>()

  const callIdRef = useRef(0)

  const send = useCallbackRef(async (...args: Parameters<T>) => {
    const currentId = ++callIdRef.current

    setLoading(true)

    try {
      const result = await cb(...args)
      if (currentId !== callIdRef.current) {
        return
      }
      onSuccess?.(result)
      setData(result)
      setError(undefined)
    } catch (err) {
      if (currentId !== callIdRef.current) {
        return
      }

      if (showErrorNotification) {
        showErrorToast(processErrorResponse(err))
      }

      onError?.(err)
      setData(undefined)
      setError(err)
    }

    setLoading(false)
  })

  const reset = useCallbackRef(() => {
    ++callIdRef.current

    setLoading(false)
    setData(undefined)
    setError(undefined)
  })

  return { isLoading, data, error, send, reset }
}
